
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import { UnsplashImage } from "../../../futurelayer/api/unsplash";
import { ImageControlAndInfo } from "@/futurelayer/images/types";
import { callImageService } from "@/futurelayer/api/image-bridge";
import { isUnsplashImage } from "@/futurelayer/images/dimensions";
import Icon from "@/components/UI/Icon.vue";
import FutureImageThumbnail from "./FutureImageThumbnail.vue";
import { logEvent } from "@/util/tracking";
import { debounce } from "lodash";

@Component({
  components: {
    TextInput,
    NiceButton,
    Icon,
    FutureImageThumbnail
  }
})
export default class FutureLayerImageBrowser extends Vue {
  @Prop({ default: null })
  imageControlData!: ImageControlAndInfo | null;

  searchKeyword = "";
  searchResults: UnsplashImage[] = [];
  isSearching = false;
  currentSearchTerm = "";
  currentPage = 1;

  hasMoreImages = true;

  get aiKeywords(): string[] {
    return this.imageControlData?.keywords || [];
  }

  get displayedImages(): UnsplashImage[] {
    if (this.searchResults.length > 0) {
      return this.searchResults;
    }
    return this.imageControlData?.searchResults || [];
  }

  debouncedLogSearchFn = debounce((value: string) => {
    logEvent("FutureLayer Stock Image Search", {
      searchTerm: value
    });
  }, 1000);

  mounted() {
    if (this.aiKeywords.length > 0) {
      this.searchKeyword = this.aiKeywords[0];
      this.currentSearchTerm = this.aiKeywords[0];
    }
  }

  async loadMoreImages() {
    if (!this.searchKeyword.trim()) return;
    this.isSearching = true;
    this.currentPage += 1;

    try {
      const results = await callImageService(
        {
          query: this.searchKeyword,
          page: this.currentPage,
          per_page: 12
        },
        new AbortController()
      );
      if (results.length > 0) {
        this.searchResults = [...this.searchResults, ...results];
      } else {
        this.hasMoreImages = false;
      }
    } catch (error) {
      console.error("Image search failed:", error);
    } finally {
      this.isSearching = false;
    }
  }

  useKeyword(keyword: string) {
    this.searchKeyword = keyword;
    this.searchImages();
  }

  async searchImages() {
    if (!this.searchKeyword.trim()) return;

    this.isSearching = true;
    this.currentSearchTerm = this.searchKeyword;
    this.currentPage = 1;
    this.hasMoreImages = true;

    try {
      const results = await callImageService(
        {
          query: this.searchKeyword,
          page: this.currentPage,
          per_page: 20
        },
        new AbortController()
      );
      this.searchResults = results;
      this.hasMoreImages = results.length > 0;
    } catch (error) {
      console.error("Image search failed:", error);
      this.searchResults = [];
      this.hasMoreImages = false;
    } finally {
      this.isSearching = false;
    }
  }

  previewImage(image: UnsplashImage) {
    // Use the same URL that's displayed in the grid to avoid loading delay
    const previewUrl = image.urls.regular || "";
    this.$emit("image-previewed", previewUrl);
  }

  restoreOriginalImage() {
    this.$emit("image-restored");
  }

  selectImage(image: UnsplashImage) {
    this.$emit("image-selected", image);
  }

  getImageSource(image: UnsplashImage): string {
    return isUnsplashImage(image) ? "Unsplash" : "Pexels";
  }

  @Watch("searchKeyword")
  onSearchKeywordChange() {
    this.debouncedLogSearchFn(this.searchKeyword);
  }
}
